/** DT One / gift & top-up catalog */
export const PROVIDER_DT = 1;
/** A2P / A2P Messaging */
export const PROVIDER_A2P = 2;
/** G2G / eVouchers */
export const PROVIDER_G2G = 3;

/**
 * Preserve catalog provider_id (1, 2, 3, …).
 * Missing / invalid → DT (1) for backward compatibility.
 */
export function normalizeProviderId(source) {
  let raw;
  if (source == null || typeof source !== 'object') {
    raw = source;
  } else {
    raw = source.provider_id ?? source.providerId;
  }
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;
  return PROVIDER_DT;
}

/**
 * Cart mixing rule: A2P (2) cannot share a cart with gift/voucher providers (1, 3, …).
 * Distinct gift providers (e.g. DT + G2G) may coexist; discount still uses real provider_id.
 */
export function cartProviderFamily(providerId) {
  return Number(providerId) === PROVIDER_A2P ? PROVIDER_A2P : PROVIDER_DT;
}

export function cartItemProviderId(item) {
  if (item == null) return PROVIDER_DT;
  if (item.provider_id != null) return normalizeProviderId(item);
  return normalizeProviderId(item.product);
}

/** Unique provider ids present in cart (real catalog ids). */
export function getCartProviderIds(cartItems) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return [];
  const set = new Set();
  for (const item of cartItems) {
    set.add(cartItemProviderId(item));
  }
  return [...set];
}

/** True when cart mixes A2P with gift/voucher lines. */
export function cartHasMixedProviderFamilies(cartItems) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return false;
  const families = new Set(cartItems.map((item) => cartProviderFamily(cartItemProviderId(item))));
  return families.size > 1;
}

/** Single provider for a homogeneous cart; null if empty or mixed real provider ids. */
export function unifiedCartProviderId(cartItems) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return null;
  const first = cartItemProviderId(cartItems[0]);
  for (let i = 1; i < cartItems.length; i++) {
    if (cartItemProviderId(cartItems[i]) !== first) return null;
  }
  return first;
}

function itemDisplayName(item) {
  const p = item?.product;
  return (
    p?.name ||
    item?.product_name ||
    (item?.product_id != null || p?.id != null
      ? `Product #${item.product_id ?? p?.id}`
      : 'Unknown product')
  );
}

/**
 * If cart is non-empty and incoming product has a different provider *family*, return conflict payload.
 * Otherwise null.
 */
export function getAddToCartProviderConflict(product, cartItems) {
  if (!product || !Array.isArray(cartItems) || cartItems.length === 0) return null;
  const incomingFamily = cartProviderFamily(normalizeProviderId(product));
  const conflicting = cartItems.filter(
    (item) => cartProviderFamily(cartItemProviderId(item)) !== incomingFamily
  );
  if (conflicting.length === 0) return null;
  const existingProviderId = cartItemProviderId(cartItems[0]);
  return {
    error: 'PROVIDER_CONFLICT',
    incomingProviderId: normalizeProviderId(product),
    existingProviderId,
    conflictingProductNames: conflicting.map(itemDisplayName),
  };
}

/** Copy for in-app modal (no technical provider names). */
export function getProviderConflictModalContent(conflict) {
  if (!conflict || conflict.error !== 'PROVIDER_CONFLICT') return null;
  const items = conflict.conflictingProductNames || [];
  const cartLabel =
    cartProviderFamily(conflict.existingProviderId) === PROVIDER_A2P
      ? 'A2P Messaging'
      : 'gift cards and vouchers';
  const newLabel =
    cartProviderFamily(conflict.incomingProviderId) === PROVIDER_A2P
      ? 'A2P Messaging'
      : 'gift cards and vouchers';
  return {
    title: 'Some vouchers in this cart cannot co-exist',
    body: `Your cart currently has ${cartLabel}. To add this ${newLabel} product, remove the items below first—or clear the cart to continue.`,
    items,
  };
}
